import { httpClient } from '@shared/api/http-client';
import { authStore } from '@shared/stores/auth-store';
import type { AutoSaveConfig, CacheData } from '@features/service-form/service-form-types';
import type { RepeatingFieldGroup } from '@features/service-form/repeating-field-group';

export class FormAutoSave {
  private config: Required<AutoSaveConfig>;
  private cacheKey: string;
  private form: HTMLFormElement | null = null;
  private groups: Map<string, RepeatingFieldGroup> = new Map();
  private groupFieldNames: Set<string> = new Set();
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(config: AutoSaveConfig) {
    this.config = {
      debounceMs: 500,
      serverPrefill: false,
      excludeFromPrefill: [],
      ...config,
    };
    this.cacheKey = `ea-form-${config.formType}-cache`;
  }

  attach(form: HTMLFormElement): void {
    this.form = form;
    const handler = () => this.scheduleSave();
    form.addEventListener('input', handler);
    form.addEventListener('change', handler);
  }

  registerGroup(containerId: string, group: RepeatingFieldGroup): void {
    this.groups.set(containerId, group);
    group.getFieldNames().forEach(name => this.groupFieldNames.add(name));
  }

  scheduleSave(): void {
    if (this.debounceTimer !== null) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.saveNow(), this.config.debounceMs);
  }

  async restore(): Promise<void> {
    const cached = this.loadFromCache();
    if (cached) {
      this.applyData(cached);
      return;
    }

    if (this.config.serverPrefill) {
      const serverData = await this.loadFromServer();
      if (serverData) {
        this.applyData(serverData);
        this.saveNow();
      }
    }
  }

  saveNow(): void {
    if (!this.form) return;
    const data = this.collectData();
    try {
      localStorage.setItem(this.cacheKey, JSON.stringify(data));
    } catch {
      // quota exceeded — silently skip
    }
  }

  clear(): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    localStorage.removeItem(this.cacheKey);
    localStorage.removeItem('ea-form-checklist');
  }

  getCachedData(): CacheData | null {
    return this.loadFromCache();
  }

  private collectData(): CacheData {
    const data: CacheData = { fields: {}, groups: {} };

    if (this.form) {
      this.form
        .querySelectorAll<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >('input, textarea, select')
        .forEach(el => {
          if (!el.name || el.type === 'file') return;

          const baseName = el.name.endsWith('[]') ? el.name.slice(0, -2) : el.name;
          if (this.groupFieldNames.has(baseName)) return;

          if (el instanceof HTMLInputElement) {
            if (el.type === 'radio' || el.type === 'checkbox') {
              if (el.checked) data.fields[el.name] = el.value;
            } else if (el.type !== 'hidden') {
              data.fields[el.name] = el.value;
            }
          } else {
            data.fields[el.name] = el.value;
          }
        });
    }

    for (const [id, group] of this.groups) {
      data.groups[id] = group.getItems();
    }

    return data;
  }

  private applyData(data: CacheData): void {
    if (this.form) {
      Object.entries(data.fields).forEach(([name, value]) => {
        const inputs = this.form!.querySelectorAll<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >(`[name="${name}"]`);
        inputs.forEach(el => {
          if (el instanceof HTMLInputElement && (el.type === 'radio' || el.type === 'checkbox')) {
            el.checked = el.value === value;
          } else {
            el.value = value;
          }
        });
      });
    }

    for (const [id, group] of this.groups) {
      const rows = data.groups[id];
      if (rows && rows.length > 0) {
        group.setItems(rows);
      }
    }
  }

  private loadFromCache(): CacheData | null {
    try {
      const raw = localStorage.getItem(this.cacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as CacheData;
      if (!parsed.fields || !parsed.groups) return null;
      return parsed;
    } catch {
      localStorage.removeItem(this.cacheKey);
      return null;
    }
  }

  private async loadFromServer(): Promise<CacheData | null> {
    if (!authStore.isAuthenticated()) return null;

    try {
      const response = await httpClient.get(`/api/form-latest?type=${this.config.formType}`);

      if (!response.success || !response.data) return null;

      const rawStr =
        typeof (response.data as Record<string, unknown>)['data'] === 'string'
          ? ((response.data as Record<string, unknown>)['data'] as string)
          : null;

      if (!rawStr) return null;

      const raw = JSON.parse(rawStr) as Record<string, unknown>;
      return this.convertServerData(raw);
    } catch {
      return null;
    }
  }

  private convertServerData(raw: Record<string, unknown>): CacheData {
    const excluded = new Set(this.config.excludeFromPrefill);
    const data: CacheData = { fields: {}, groups: {} };

    // Convert group arrays first
    for (const [id, group] of this.groups) {
      const fieldNames = group.getFieldNames();
      const rows: Record<string, string>[] = [];
      let maxLen = 0;

      const columnValues: Record<string, string[]> = {};
      fieldNames.forEach(fieldName => {
        if (excluded.has(fieldName)) return;
        const serverKey = `${fieldName}[]`;
        const val = raw[serverKey];
        if (Array.isArray(val)) {
          columnValues[fieldName] = val.map(String);
          maxLen = Math.max(maxLen, val.length);
        }
      });

      for (let i = 0; i < maxLen; i++) {
        const row: Record<string, string> = {};
        fieldNames.forEach(name => {
          row[name] = columnValues[name]?.[i] ?? '';
        });
        rows.push(row);
      }

      if (rows.length > 0) data.groups[id] = rows;
    }

    // Remaining scalar fields
    Object.entries(raw).forEach(([key, value]) => {
      if (key.endsWith('[]')) return;
      if (excluded.has(key)) return;
      if (typeof value === 'string' || typeof value === 'number') {
        data.fields[key] = String(value);
      }
    });

    return data;
  }
}
